import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import NFTCard from './NFTCard';

const NFTGrid = ({
  nfts,
  account,
  mode,
  onBuyNFT,
  onListForSale,
  onCancelListing,
  onViewDetails,
  onRenewSuccess,
}) => {
  const [visibleNFTs, setVisibleNFTs] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const observerRef = useRef(null);
  const ITEMS_PER_PAGE = 8; // Load 8 items initially

  // Memoize filtered data to prevent unnecessary recalculations
  const filteredData = useMemo(() => {
    if (!nfts || nfts.length === 0) return [];
    return mode === 'My'
      ? nfts.filter((nft) => nft.owner?.toLowerCase() === account?.toLowerCase() || nft.seller?.toLowerCase() === account?.toLowerCase())
      : nfts;
  }, [nfts, account, mode]);

  // Use refs to track previous values and prevent unnecessary resets
  const prevFilteredDataRef = useRef(null);
  const prevModeRef = useRef(mode);

  // Intersection Observer for lazy loading
  const loadMoreNFTs = useCallback(() => {
    if (isLoadingMore || !hasMore || filteredData.length === 0) return;
    
    setIsLoadingMore(true);
    
    // Use requestAnimationFrame for smoother loading
    requestAnimationFrame(() => {
      setVisibleNFTs(prev => {
        const currentLength = prev.length;
        const nextNFTs = filteredData.slice(currentLength, currentLength + ITEMS_PER_PAGE);
        const newVisible = [...prev, ...nextNFTs];
        
        // Check if there are more items to load
        if (currentLength + ITEMS_PER_PAGE >= filteredData.length) {
          setHasMore(false);
        }
        
        setIsLoadingMore(false);
        return newVisible;
      });
    });
  }, [filteredData, isLoadingMore, hasMore]);

  // Reset visible NFTs only when data actually changes (e.g. mode change or complete refresh)
  useEffect(() => {
    const modeChanged = prevModeRef.current !== mode;
    
    // Check if the data is fundamentally different (not just appended)
    // We check if the first item has changed or if items were removed
    const firstItemId = filteredData[0]?.tokenId?.toString() || filteredData[0]?.id;
    const prevFirstItemId = prevFilteredDataRef.current?.split('|')[0];
    const dataShrank = prevFilteredDataRef.current && filteredData.length < prevFilteredDataRef.current.split('|').length;
    
    const shouldReset = modeChanged || 
                       prevFilteredDataRef.current === null || 
                       (firstItemId !== prevFirstItemId) ||
                       dataShrank;

    if (shouldReset) {
      requestAnimationFrame(() => {
        setVisibleNFTs(filteredData.slice(0, ITEMS_PER_PAGE));
        setHasMore(filteredData.length > ITEMS_PER_PAGE);
        setIsLoadingMore(false);
      });
    } else {
      // If data was just appended, just update hasMore
      // visibleNFTs stays as is, allowing the IntersectionObserver to handle the next batch
      setHasMore(filteredData.length > visibleNFTs.length);
    }
    
    // Update ref with current state
    const dataKey = filteredData.length > 0 
      ? filteredData.map(n => n.tokenId?.toString() || n.id).join('|')
      : 'empty';
    prevFilteredDataRef.current = dataKey;
    prevModeRef.current = mode;
  }, [filteredData, mode, visibleNFTs.length]);

  // Setup intersection observer
  useEffect(() => {
    if (!observerRef.current || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && !isLoadingMore && hasMore) {
          loadMoreNFTs();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = observerRef.current;
    observer.observe(currentRef);

    return () => {
      observer.disconnect();
    };
  }, [loadMoreNFTs, isLoadingMore, hasMore]);

  return (
    <div style={{ position: 'relative', minHeight: 'inherit' }}>
      {/* Grid of visible NFTs with locked layout */}
      <div 
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        style={{ 
          contain: 'layout style', // Isolate layout and style calculations
          willChange: 'auto', // Hint to browser for optimization
          position: 'relative', // Needed for z-index and other positioning contexts
          gridAutoRows: 'minmax(500px, auto)' // Ensure every row starts with at least the card min-height
        }}
      >
        {visibleNFTs.map((nft) => {
          // Use stable key based on tokenId only
          const uniqueKey = `nft-${nft.tokenId?.toString() || nft.id}`;
          return (
            <NFTCard
              key={uniqueKey}
              nft={nft}
              account={account}
              mode={mode}
              onBuy={onBuyNFT}
              onListForSale={onListForSale}
              onCancelListing={onCancelListing}
              onViewDetails={onViewDetails}
              onRenewSuccess={onRenewSuccess}
            />
          );
        })}
      </div>

      {/* Hidden sentinel for intersection observer - no UI, just triggers loading */}
      {hasMore && (
        <div 
          ref={observerRef}
          style={{ 
            height: '1px', 
            width: '100%',
            marginTop: '100px', // Give some margin for the observer to trigger before reaching the very bottom
            pointerEvents: 'none', // Ensure it doesn't interfere with user interaction
            visibility: 'hidden', // Make it invisible
            position: 'absolute', // Position it relative to the parent div
            bottom: '-100px' // Position it below the grid to trigger early
          }}
          aria-hidden="true"
        />
      )}

      {/* No NFTs state */}
      {!nfts || nfts.length === 0 ? (
        <div className="text-center py-20">
          <div className="relative mx-auto mb-8">
            <div className="w-32 h-32 bg-gradient-to-br from-gray-600/20 to-gray-500/20 backdrop-blur-sm rounded-3xl border border-white/10 flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 animate-pulse"></div>
              <span className="text-5xl relative z-10">🖼️</span>
            </div>
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl blur-xl opacity-30 animate-pulse"></div>
          </div>
          
          <h3 className="text-3xl font-bold text-white mb-4 bg-gradient-to-r from-gray-100 to-gray-300 bg-clip-text text-transparent">
            {mode === 'All' ? 'No NFTs Listed' : 'No NFTs Owned'}
          </h3>
          
          <p className="text-gray-400 text-lg max-w-md mx-auto mb-8 leading-relaxed">
            {mode === 'All'
              ? 'No NFTs are currently listed for sale. Be the first to list yours!'
              : 'You don\'t own any NFTs yet. Mint some to get started!'}
          </p>
          
          <div className="mt-12 flex justify-center gap-8 text-gray-500 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
              <span>Marketplace is live</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse delay-75"></div>
              <span>Ready for trading</span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default NFTGrid;
